import React, {useState} from 'react';
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Spinner from "react-bootstrap/Spinner";
import {
    DispensingOrdersFuelsStock,
    DispensingOrdersViewState,
    IDispensingOrder, IDispensingOrderEditRequestForm,
    IDispensingOrderForm,
    IDispensingOrderFuel, IDispensingOrderModifiedFuelRequestRow,
    IDispensingOrderRequestForm,
    IDispensingOrderRequestRow,
    ISavingProgress
} from "./models";
import {DispensingOrdersService} from "./DispensingOrders.service";
import moment from "moment";
import {dateFormat, isoDateFormat} from "../../common/dateFormatterConfig";

interface IModalProps {
    isVisible: boolean;
    customerId: number | null;
    warehouse: DispensingOrdersFuelsStock;
    formData: IDispensingOrderForm;
    selectedFuels: Array<IDispensingOrderFuel>;
    handleModalToggle: (refresh: boolean) => void;
    viewState: DispensingOrdersViewState;
    editingDispensingOrder?: IDispensingOrder;
}

export function DispensingOrdersConfirmationModal(props: IModalProps) {
    const [saving, setSaving]: [boolean, any] = useState(false);
    const [savingError, setSavingError]: [boolean, any] = useState(false);

    const confirmChanges = () => {
        const customerId = props.customerId;
        if (customerId == null)
            return;

        setSaving(true);

        if (props.viewState === DispensingOrdersViewState.NEW) {
            const form: IDispensingOrderRequestForm = {
                documentNumber: props.formData.documentNumber,
                owner: props.formData.owner,
                carrier: props.formData.carrier,
                vehicleCode: props.formData.car,
                driver: props.formData.driver,
                validUntil: moment(props.formData.validUntil, dateFormat).format(isoDateFormat),
                warehouse: props.warehouse,
                rows: props.selectedFuels.map(item =>
                    ({
                        batchAmount: Number(item.batchAmount),
                        remoteBatchId: Number(item.remoteBatchId),
                        dosBatchAmount: item.isDosVisible ? Number(item.dosBatchAmount) : null,
                        remoteDosBatchId: item.isDosVisible ? Number(item.remoteDosBatchId) : null
                    } as IDispensingOrderRequestRow))
            };

            DispensingOrdersService.postDispensingOrder(customerId, form).then((uid) => {
                pollSavingProgress(customerId, uid)
            }).catch(reason => {
                console.error('Saving failed: ', reason);
                setSavingError(true);
                setSaving(false);
            });
        } else if (props.viewState === DispensingOrdersViewState.EDIT) {
            if (props.editingDispensingOrder === undefined) {
                return;
            }

            const modifiedRows: Array<IDispensingOrderModifiedFuelRequestRow> = [];
            const newRows: Array<IDispensingOrderRequestRow> = [];

            for (const selectedFuel of props.selectedFuels) {
                const existingFuel = props.editingDispensingOrder.productRows
                    .find(fuel => fuel.remoteBatchId.toString() === selectedFuel.remoteBatchId);
                if (existingFuel !== undefined) {
                    modifiedRows.push({
                        productName: existingFuel.productName,
                        rowNumber: existingFuel.rowNumber,
                        quantity: Number(selectedFuel.batchAmount)
                    });
                }
                const existingDosFuel = props.editingDispensingOrder.productRows
                    .find(fuel => (fuel.remoteBatchId.toString() === selectedFuel.remoteDosBatchId) && selectedFuel.isDosVisible);

                if (existingDosFuel !== undefined) {
                    modifiedRows.push({
                        productName: existingDosFuel.productName,
                        rowNumber: existingDosFuel.rowNumber,
                        quantity: Number(selectedFuel.dosBatchAmount)
                    });
                }

                const noPreExistingFuel = existingFuel === undefined;
                const noPreExistingDosFuel = selectedFuel.isDosVisible && existingDosFuel === undefined;
                if (noPreExistingFuel || noPreExistingDosFuel) {
                    newRows.push({
                        batchAmount: noPreExistingFuel ? Number(selectedFuel.batchAmount) : null,
                        remoteBatchId: noPreExistingFuel ? Number(selectedFuel.remoteBatchId) : null,
                        dosBatchAmount: noPreExistingDosFuel ? Number(selectedFuel.dosBatchAmount) : null,
                        remoteDosBatchId: selectedFuel.isDosVisible ? Number(selectedFuel.remoteDosBatchId) : null
                    } as IDispensingOrderRequestRow);
                }
            }

            for (const fuel of props.editingDispensingOrder.productRows) {
                if (!props.selectedFuels.find(selectedFuel =>
                    (Number(selectedFuel.remoteBatchId) === fuel.remoteBatchId) ||
                    (Number(selectedFuel.remoteDosBatchId) === fuel.remoteBatchId))) {
                    modifiedRows.push({
                        productName: fuel.productName,
                        rowNumber: fuel.rowNumber,
                        quantity: 0
                    });
                }
            }

            const form: IDispensingOrderEditRequestForm = {
                uid: props.editingDispensingOrder.uid,
                documentNumber: props.formData.documentNumber,
                owner: props.formData.owner,
                carrier: props.formData.carrier,
                vehicleCode: props.formData.car,
                driver: props.formData.driver,
                validUntil: moment(props.formData.validUntil, dateFormat).format(isoDateFormat),
                warehouse: props.warehouse,
                newRows: newRows,
                modifiedRows: modifiedRows
            };

            DispensingOrdersService.putDispensingOrder(customerId, form).then((uid) => {
                pollSavingProgress(customerId, uid)
            }).catch(reason => {
                console.error('Saving failed: ', reason);
                setSavingError(true);
                setSaving(false);
            });
        }
    };

    const pollSavingProgress = (customerId: number, uid: string) => {
        DispensingOrdersService.getDispensingOrderCreationProgress(customerId, uid, props.editingDispensingOrder ? 'edit' : 'save')
            .then((value: ISavingProgress) => {
            if (value.state === "RUNNING") {
                setTimeout(() => {
                    pollSavingProgress(customerId, uid)
                }, 6 * 1000);
                return;
            }
            if (value.state === "FAILED") {
                setSaving(false);
                setSavingError(true);

                return;
            }
            if (value.state === "COMPLETED") {
                setSaving(false);
                props.handleModalToggle(true);

                return;
            }
        }).catch(reason => {
            console.error('Polling save state failed: ', reason);
            setSavingError(true);
            setSaving(false);
        })
    };

    const selectedFuelRows = props.selectedFuels.map(fuel => {
        return (
            <tr key={fuel.productName}>
                <td>{fuel.productName}</td>
                <td>{fuel.batchAmount ? fuel.batchAmount + ' L' : '-'} </td>
                <td>{fuel.dosBatchAmount ? fuel.dosBatchAmount + ' L' : '-'} </td>
            </tr>
        )
    });

    return (
        <>
            <Modal
                size={"xl"}
                show={props.isVisible}
                onHide={() => props.handleModalToggle(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        Kinnita väljastuskorraldus
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="d-flex flex-column">
                        <table className="table table-hover table-responsive-md dispensing-order-fuels__table">
                            <thead>
                                <tr>
                                    <th>Kütus</th>
                                    <th>Kogus</th>
                                    <th>DOS</th>
                                </tr>
                            </thead>
                            <tbody>
                                {selectedFuelRows}
                            </tbody>
                        </table>
                        <div className="row border-bottom mb-5"/>

                        <table className="table table-responsive-md table-hover dispensing-order-form__table">
                            <tbody>
                                <tr>
                                    <td><h2>Alusdokument</h2></td>
                                    <td>{props.formData.documentNumber}</td>
                                </tr>
                                <tr>
                                    <td><h2>Kauba omanik</h2></td>
                                    <td>{props.formData.ownerLabel}</td>
                                </tr>
                                <tr>
                                    <td><h2>Vedaja</h2></td>
                                    <td>{props.formData.carrierLabel}</td>
                                </tr>
                                <tr>
                                    <td><h2>Auto</h2></td>
                                    <td>{props.formData.carLabel}</td>
                                </tr>
                                <tr>
                                    <td><h2>Juht</h2></td>
                                    <td>{props.formData.driverLabel}</td>
                                </tr>
                                <tr>
                                    <td><h2>Kehtib kuni</h2></td>
                                    <td>{props.formData.validUntil}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </Modal.Body>
                <Modal.Footer className="d-flex flex-row align-items-end flex-wrap mb-1">
                    <div className="col-5 d-flex justify-content-center">
                        <Button variant="outline-dark"
                                onClick={() => props.handleModalToggle(false)}
                                type="button"
                                disabled={saving}
                                className={"d-flex align-items-center"}>
                            <span className='px-2'>Loobu</span>
                        </Button>
                    </div>
                    <div className="col-12 col-md-6 d-flex justify-content-center flex-row">
                        <Button variant="success" onClick={confirmChanges} type="button" disabled={saving}
                                className="d-flex align-items-center">
                            {
                                !saving &&
                                <span className='px-2'>Kinnita</span>
                            }
                            {
                                saving &&
                                <>
                                    <span className='px-2'>Salvestab…</span>
                                    <Spinner size={"sm"} animation={"border"}/>
                                </>
                            }
                        </Button>
                        {
                            !saving && savingError &&
                            <span className="d-flex align-items-center text-danger px-2">Viga salvestamisel, proovi mõne hetke pärast uuesti.</span>
                        }
                    </div>
                </Modal.Footer>
            </Modal>
        </>
    );
}
